
import { DateTime } from 'luxon';
import { tenderPhases, tenderTypes } from '~/assets/mappings.json';

export default {
  props: {
    tender: {
      type: Object,
      required: true
    },

    currentLot: {
      type: Object,
      required: true
    },

    translation: {
      type: Object,
      default: null
    },

    summaryActive: {
      type: Boolean
    }
  },

  data () {
    return {
      tenderPhases,
      tenderTypes,
      publications: []
    };
  },

  computed: {
    isExpected () {
      return this.tender.status === 'Expected';
    },

    latestVersion () {
      if (this.tender.preview) {
        return {
          title: this.tender.title,
          publications: [],
          lots: [this.currentLot]
        };
      }

      if (this.tender.tenderVersions.length === 0) {
        throw new Error('This tender is missing versions.');
      }

      return this.tender.tenderVersions[0];
    },

    sourceURL () {
      return this.publication?.publicationUrl;
    },

    source () {
      return this.publication?.tenderSource?.name;
    },

    publication () {
      if (this.latestVersion.publications?.length < 1) {
        return;
      }

      return (
        this.latestVersion.publications.find(x => x.isApplicationPlatform) ??
        this.latestVersion.publications[0]
      );
    },

    lots () {
      if (this.tender.preview) { return []; }
      return [...this.latestVersion.lots].sort(
        (a, b) => a.lotNumber.localeCompare(b.lotNumber, { numeric: true, ignorePunctuation: true }));
    },

    organizations () {
      if (this.tender.preview) {
        return [{
          id: '-',
          name: 'Altura B.V.',
          phone: '0912345678',
          email: 'info@altura.io',
          website: 'altura.io',
          contacts: [
            {
              id: '1e8626cb-2704-4b07-a61f-ea548a41b82e',
              name: 'Customer Support',
              phones: [
                { phoneNumber: '0912345678' }
              ],
              emails: [
                { emailAddress: 'info@altura.io' }
              ],
              linkedInUrl: null
            }
          ]
        }];
      }

      return [...this.tender.organizations].sort(a => a.isAdvisor);
    },

    description () {
      let description = '';
      if (this.tender.preview) { description = this.tender.description; }
      if (this.translation) {
        description = this.translation.shortDescription;
        if ((this.currentLot.id in this.translation.lots) &&
        this.translation.shortDescription.length <
          this.translation.lots[this.currentLot.id].description.length
        ) {
          description = this.translation.lots[this.currentLot.id].description;
        }
        return description;
      }

      if (this.currentLot.description !== null) {
        if (
          this.latestVersion.shortDescription &&
          this.latestVersion.shortDescription.length >
          this.currentLot.description.length
        ) {
          description = this.latestVersion.shortDescription;
        } else {
          description = this.currentLot.description;
        }
      } else {
        description = this.latestVersion.shortDescription ?? '';
      }

      return description?.trim?.()?.replace?.(/[\u00A0]/gu, _ => '&nbsp;') ?? '';
    },

    value () {
      if (this.tender.preview) {
        return;
      }

      const sumOfTotalLotPrice = this.latestVersion.lots.reduce(
        (p, c) => p + (c.bid?.totalPrice ?? 0),
        0
      );
      const sumOfEstimatedLotPrice = this.latestVersion.lots.reduce(
        (p, c) => p + (c.estimatedPrice ?? 0),
        0
      );

      if (this.currentLot.bid?.totalPrice > 10) {
        return {
          field: 'lot.bid.totalPrice',
          value: this.currentLot.bid.totalPrice,
          totalField: 'sumOfTotalLotPrice',
          totalValue: sumOfTotalLotPrice
        };
      }

      if (this.currentLot.estimatedPrice > 10) {
        return {
          field: 'lot.estimatedPrice',
          value: this.currentLot.estimatedPrice,
          totalField: 'sumOfEstimatedLotPrice',
          totalValue: sumOfEstimatedLotPrice
        };
      }

      if (this.latestVersion.value > 10) {
        return {
          field: 'value',
          value: this.latestVersion.value
        };
      }

      if (this.latestVersion.estimatedValue > 10) {
        return {
          field: 'estimatedValue',
          value: this.latestVersion.estimatedValue
        };
      }

      return null;
    },

    statusColor () {
      switch (this.tender.status) {
      case 'Expected':
        return 'base-blue';
      case 'Active':
        return 'secondary-500';
      case 'Closed':
        return 'error-500';
      }

      return 'base-coral';
    },

    requirements () {
      const result = [];

      if (this.tender.preview) { return []; }
      const requirements = this.translation ? [...this.translation.requirements] : [...this.latestVersion.requirements];
      requirements.sort(
        (a, b) => b.order - a.order
      );

      for (const requirement of requirements) {
        const element = result.find(x => x.group === requirement.group);
        if (requirement.description === '-') {
          continue;
        }

        if (element) {
          element.conditions.unshift(requirement.description);
          continue;
        }

        result.unshift({
          group: requirement.group,
          conditions: [requirement.description]
        });
      }

      return result;
    },

    location () {
      if (this.tender.preview) {
        return {
          code: 'xl',
          displayName: 'Far far away'
        };
      }

      // find longest nuts code
      return this.currentLot.nuts.reduce(
        (p, c) => (p.code.length > c.code.length ? p : c),
        { code: '', displayName: 'Onbekend' }
      );
    },

    deadline () {
      return this.currentLot.planningItems.find(
        x => x.type === 'ApplicationDeadlineDate'
      );
    },

    sortedDates () {
      return [...this.currentLot.planningItems].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
    },

    extensionDate () {
      const today = DateTime.now().startOf('day').toJSDate();

      // get the first ExtensioDate or EndDate after today from sortedDates
      const dates = this.sortedDates.filter(
        x => x.type === 'ExtensionDate' || x.type === 'EndDate'
      );

      const date = [...dates].reverse().find(x => new Date(x.date) > today);

      if (date) {
        return date;
      }

      return dates[0];
    },

    cpvCodes () {
      const cpvs = [];

      if (this.tender.preview) { return []; }

      for (const cpv of [...this.latestVersion.cpvs, ...this.currentLot.cpvs]) {
        if (!cpvs.some(c => c.code === cpv.code)) {
          cpvs.push(cpv);
        }
      }

      return cpvs;
    },

    tenderPhase () {
      if (this.tender.preview || !this.publication || this.publication.tenderPhase === 'Unknown') {
        return 'Onbekend';
      }

      return this.publication.tenderPhase;
    },

    awardCriteria () {
      if (this.translation && this.currentLot.id in this.translation.lots && !!this.translation.lots[this.currentLot.id]) {
        return this.translation.lots[this.currentLot.id].awardCriterias;
      }

      return this.currentLot.awardCriterias;
    }
  },

  methods: {
    formatLotNumber (lotName) {
      if (/LOT-\d{4}/.test(lotName)) {
        return Number(lotName.split('-')[1]);
      }

      return lotName;
    }

  }
};
